import { useState, useEffect } from "react";
import { z } from "zod";
import useAuth, { SUPERUSER_BYPASS_EMAIL, SUPERUSER_BYPASS_ENABLED } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Gift, Mail, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signInWithEmail, signInWithPassword, bypassSuperuserLogin, checkUserHasPassword, verifyOtp, user } = useAuth();

  const [authMode, setAuthMode] = useState<'email' | 'otp' | 'password'>('email');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [referralCode, setReferralCode] = useState("");
  const isPasswordOnly = email.toLowerCase() === SUPERUSER_BYPASS_EMAIL;

  useEffect(() => {
    if (user) {
      navigate('/map', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const emailSchema = z.string().min(1, "Email is required").email("Invalid email address");
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setIsLoading(false);
      toast({ title: "Invalid email", description: parsed.error.errors[0].message, variant: "destructive" });
      return;
    }

    // Validate referral code if entered
    if (referralCode.trim()) {
      const { data: refData, error: refError } = await supabase
        .from("referral_codes")
        .select("id, code")
        .ilike("code", referralCode.trim())
        .eq("is_active", true)
        .maybeSingle();

      if (refError || !refData) {
        setIsLoading(false);
        toast({ title: "Invalid referral code", description: "The referral code you entered is not valid.", variant: "destructive" });
        return;
      }

      localStorage.setItem("giftmap_simple_referral", refData.code);
    }

    if (SUPERUSER_BYPASS_ENABLED && parsed.data.toLowerCase() === SUPERUSER_BYPASS_EMAIL) {
      const { error } = bypassSuperuserLogin(parsed.data);
      setIsLoading(false);

      if (error) {
        toast({ title: "Bypass failed", description: error.message, variant: "destructive" });
        return;
      }

      toast({ title: "Welcome back", description: "Superuser login bypass applied." });
      return;
    }

    // Check if user has a password set
    const hasPassword = await checkUserHasPassword(email);
    
    if (hasPassword) {
      // User has password, show password input
      setAuthMode('password');
      setIsLoading(false);
    } else {
      // New user or no password set, send OTP
      const { error } = await signInWithEmail(email);
      setIsLoading(false);

      if (error) {
        const errorMessage = error?.message || String(error);
        
        let userFriendlyMessage = "Unable to send code. Please try again.";
        
        if (errorMessage.includes('rate')) {
          userFriendlyMessage = "Too many requests. Please wait a few minutes and try again.";
        } else if (errorMessage.includes('email')) {
          userFriendlyMessage = "Unable to send email. Please check your email address.";
        } else {
          userFriendlyMessage = errorMessage;
        }
        
        console.error('Failed to send OTP:', errorMessage);
        
        toast({ 
          title: "Failed to send code", 
          description: userFriendlyMessage, 
          variant: "destructive" 
        });
        return;
      }

      setOtpSent(true);
      setAuthMode('otp');
      toast({ title: "Code sent", description: `We sent a 6-digit code to ${email}` });
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const passwordSchema = z.string().min(8, "Password must be at least 8 characters");
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setIsLoading(false);
      toast({ title: "Invalid password", description: parsed.error.errors[0].message, variant: "destructive" });
      return;
    }

    const { error } = await signInWithPassword(email, password);
    setIsLoading(false);

    if (error) {
      const errorMessage = error?.message || String(error);
      
      let userFriendlyMessage = "Unable to sign in. Please try again.";
      
      if (errorMessage.includes('Invalid login credentials')) {
        userFriendlyMessage = "Incorrect password. Please try again or use email code.";
      } else if (errorMessage.includes('rate')) {
        userFriendlyMessage = "Too many attempts. Please wait a few minutes and try again.";
      }
      
      console.error('Password login failed:', errorMessage);
      
      toast({ 
        title: "Login failed", 
        description: userFriendlyMessage, 
        variant: "destructive" 
      });
      return;
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const otpSchema = z
      .string()
      .regex(/^\d{6}$/g, "Enter the 6-digit code")
      .transform((s) => s.trim());
    const parsed = otpSchema.safeParse(otp);
    if (!parsed.success) {
      setIsLoading(false);
      toast({ title: "Invalid code", description: parsed.error.errors[0].message, variant: "destructive" });
      return;
    }

    const { error } = await verifyOtp(email, parsed.data);
    // Stop local spinner regardless; global auth loading is managed by useAuth
    setIsLoading(false);

    if (error) {
      const errorMessage = error?.message || String(error);
      
      let userFriendlyMessage = "Unable to verify code. Please try again.";
      let title = "Verification failed";
      
      if (errorMessage.includes('AuthRetryableFetchError') || errorMessage.includes('fetch') || errorMessage.includes('network')) {
        title = "Connection issue";
        userFriendlyMessage = "We're having trouble connecting. Please check your internet connection and try again.";
      } else if (errorMessage.includes('invalid') || errorMessage.includes('expired')) {
        userFriendlyMessage = "The code is invalid or has expired. Please request a new code.";
      } else if (errorMessage.includes('attempts')) {
        userFriendlyMessage = "Too many attempts. Please request a new code.";
      }
      
      console.error('OTP verification failed:', errorMessage);
      
      setVerificationError(true);
      
      toast({ 
        title, 
        description: userFriendlyMessage,
        variant: "destructive" 
      });
      return;
    }
    
    setVerificationError(false);
    // On success, wait for user state to update via useAuth; useEffect will navigate
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full animate-pulse-soft" />
            <div className="relative bg-gradient-sunset p-6 rounded-2xl shadow-warm">
              <Gift className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-sunset bg-clip-text text-transparent">imata</h1>
        </div>

        <Card className="shadow-warm border-border/50">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl">
              {authMode === 'password' ? "Welcome Back" : authMode === 'otp' ? "Enter Code" : "Welcome"}
            </CardTitle>
            <CardDescription>
              {authMode === 'password'
                ? `Enter your password for ${email}`
                : authMode === 'otp'
                ? `Enter the 6-digit code sent to ${email}`
                : "Enter your email to continue"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isOnline && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive mb-4">
                ⚠️ No internet connection detected. Please check your connection.
              </div>
            )}
            
            {authMode === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-border/50"
                  />
                </div>
                <div className="space-y-2">
                  <Input
                    type="text"
                    placeholder="Referral code (optional)"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className="border-border/50"
                    maxLength={20}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 shadow-soft"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    "Checking..."
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Continue
                    </>
                  )}
                </Button>
              </form>
            )}

            {authMode === 'password' && (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-2">
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-border/50"
                    minLength={8}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-1/3"
                    onClick={() => {
                      setAuthMode('email');
                      setPassword('');
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="w-2/3 bg-primary hover:bg-primary/90 shadow-soft"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      "Signing in..."
                    ) : (
                      <>
                        <KeyRound className="w-4 h-4 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                </div>
                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => navigate('/reset-password', { state: { email } })}
                    className="text-primary hover:underline"
                  >
                    Forgot password?
                  </button>
                  {!isPasswordOnly && (
                    <button
                      type="button"
                      onClick={async () => {
                        setAuthMode('otp');
                        setIsLoading(true);
                        const { error } = await signInWithEmail(email);
                        setIsLoading(false);
                        if (!error) {
                          setOtpSent(true);
                          toast({ title: "Code sent", description: `We sent a 6-digit code to ${email}` });
                        }
                      }}
                      className="text-muted-foreground hover:text-primary"
                    >
                      Use email code
                    </button>
                  )}
                </div>
              </form>
            )}

            {authMode === 'otp' && (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-1/3"
                    onClick={() => {
                      setAuthMode('email');
                      setOtpSent(false);
                      setOtp('');
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="w-2/3 bg-primary hover:bg-primary/90 shadow-soft"
                    disabled={isLoading || otp.length !== 6}
                  >
                    {isLoading ? (
                      "Verifying..."
                    ) : (
                      <>
                        <KeyRound className="w-4 h-4 mr-2" />
                        Verify Code
                      </>
                    )}
                  </Button>
                </div>
                {verificationError && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setAuthMode('email');
                      setOtpSent(false);
                      setOtp('');
                      setVerificationError(false);
                    }}
                  >
                    Request New Code
                  </Button>
                )}
              </form>
            )}

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>By continuing, you agree to our</p>
              <div className="flex justify-center gap-2 mt-1">
                <button onClick={() => navigate("/privacy")} className="text-primary hover:underline">
                  Privacy Policy
                </button>
                <span>and</span>
                <button onClick={() => navigate("/terms")} className="text-primary hover:underline">
                  Terms of Service
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-muted-foreground">
            ← Back to home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Auth;